import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useSearchParams } from "react-router-dom";
import { useWorkspace } from "contexts/WorkspaceContext";
import { useFileUpload } from "hooks/useFileUpload";
import { careService } from "services/careService";
import { conversionWarningFor, convertHeicToJpegResult } from "utils/heicConversion";

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_WORKSPACE_FILES = 50;
const PUBLIC_SCOPE = "PUBLIC";
const INTERNAL_SCOPE = "INTERNAL";
const DOCUMENT_VIEW_MODES = [
  { id: "grid", label: "Grid", icon: "ti-layout-grid" },
  { id: "flow", label: "Flow", icon: "ti-layout-board" },
  { id: "list", label: "List", icon: "ti-list" },
  { id: "compact", label: "Compact", icon: "ti-layout-list" },
];
const PHOTO_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic"];

const bytesLabel = (bytes) => {
  const value = Number(bytes || 0);
  if (!value) return "0 KB";
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const isPhoto = (fileOrDownload = {}) => {
  const mimeType = String(fileOrDownload.type || fileOrDownload.mimeType || "").toLowerCase();
  const name = String(fileOrDownload.name || fileOrDownload.fileName || "").toLowerCase();
  return mimeType.startsWith("image/") || PHOTO_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

const downloadFileTypeLabel = (file = {}) => {
  if (isPhoto(file)) return "Photo";
  const type = String(file.type || file.mimeType || "").split("/").pop();
  return type ? type.toUpperCase() : "Document";
};

const documentUrlFor = (download = {}) => download?.downloadUrl || download?.signedUrl || download?.previewUrl || "";
const isFolder = (item = {}) => String(item.itemType || "").toUpperCase() === "FOLDER";
const isFile = (item = {}) => !isFolder(item);

function WorkspaceDownloadsPage() {
  const selectedPreviewUrlsRef = useRef([]);
  const remoteBlobUrlsRef = useRef([]);
  const remotePreviewBlobUrlRef = useRef("");
  const appliedShareTargetRef = useRef("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { uploadFile, uploading } = useFileUpload();
  const [publicDownloads, setPublicDownloads] = useState([]);
  const [internalDownloads, setInternalDownloads] = useState([]);
  const [activeScope, setActiveScope] = useState(INTERNAL_SCOPE);
  const [currentFolderIds, setCurrentFolderIds] = useState({ [PUBLIC_SCOPE]: null, [INTERNAL_SCOPE]: null });
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [folderDraft, setFolderDraft] = useState({ fileName: "", description: "" });
  const [folderError, setFolderError] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [draggingDownloadId, setDraggingDownloadId] = useState("");
  const [dragOverFolderId, setDragOverFolderId] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [selectedDownloadFiles, setSelectedDownloadFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewGallerySource, setPreviewGallerySource] = useState("");
  const [previewFileIndex, setPreviewFileIndex] = useState(-1);
  const [remotePreviewBlobUrl, setRemotePreviewBlobUrl] = useState("");
  const [remotePreviewLoading, setRemotePreviewLoading] = useState(false);
  const [previewRotation, setPreviewRotation] = useState(0);

  const workspaceId = selectedWorkspaceId || selectedWorkspace?.id || "";
  const downloads = activeScope === PUBLIC_SCOPE ? publicDownloads : internalDownloads;
  const currentFolderId = currentFolderIds[activeScope] || null;
  const currentItems = downloads.filter((item) => (item.parentId || null) === currentFolderId);
  const currentFolders = currentItems.filter(isFolder).sort((a, b) => String(a.fileName || "").localeCompare(String(b.fileName || "")));
  const currentFiles = currentItems.filter(isFile);
  const isCompactView = viewMode === "compact";
  const previewGalleryItems = previewGallerySource === "staged" ? selectedDownloadFiles : currentFiles;
  const previewFile = previewFileIndex >= 0 ? previewGalleryItems[previewFileIndex] : null;
  const previewFileSourceUrl = documentUrlFor(previewFile);
  const previewFileUrl = remotePreviewBlobUrl || (previewFileSourceUrl.startsWith("blob:") ? previewFileSourceUrl : "");
  const canNavigatePreview = previewGalleryItems.length > 1 && previewFileIndex >= 0;
  const internalFileCount = internalDownloads.filter(isFile).length;
  const canUpload = internalFileCount < MAX_WORKSPACE_FILES;
  const slotsRemaining = Math.max(0, MAX_WORKSPACE_FILES - internalFileCount - selectedDownloadFiles.length);
  const folderPath = (() => {
    const path = [];
    const seen = new Set();
    let cursor = currentFolderId;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const lookupId = cursor;
      const folder = downloads.find((item) => item.id === lookupId && isFolder(item));
      if (!folder) break;
      path.unshift(folder);
      cursor = folder.parentId || null;
    }
    return path;
  })();
  const folderDialogPath = (() => {
    const path = [];
    const seen = new Set();
    let cursor = Object.prototype.hasOwnProperty.call(folderDraft, "parentId") ? folderDraft.parentId : currentFolderId;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const lookupId = cursor;
      const folder = downloads.find((item) => item.id === lookupId && isFolder(item));
      if (!folder) break;
      path.unshift(folder);
      cursor = folder.parentId || null;
    }
    return path;
  })();

  const setCurrentFolder = (folderId) => {
    const nextFolderId = folderId || null;
    setCurrentFolderIds((current) => ({ ...current, [activeScope]: nextFolderId }));
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("scope", activeScope);
      next.delete("file");
      if (nextFolderId) next.set("folder", nextFolderId);
      else next.delete("folder");
      return next;
    }, { replace: false });
  };

  const linkForDocumentTarget = (target = {}) => {
    const url = new URL(window.location.href);
    const targetScope = target.scope || target.item?.documentScope || activeScope;
    url.searchParams.set("scope", targetScope);
    url.searchParams.delete("folder");
    url.searchParams.delete("file");
    if (target.item?.id) {
      url.searchParams.set(isFolder(target.item) ? "folder" : "file", target.item.id);
    } else if (target.folderId || currentFolderId) {
      url.searchParams.set("folder", target.folderId || currentFolderId);
    }
    return url.toString();
  };

  const copyDocumentLink = async (target = {}) => {
    const link = linkForDocumentTarget(target);
    try {
      await navigator.clipboard.writeText(link);
      message.success("Share link copied.");
    } catch (error) {
      window.prompt("Copy this link", link);
    }
  };

  const loadDownloads = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [publicData, internalData] = await Promise.all([
        careService.workspaceDownloads.list({ documentScope: PUBLIC_SCOPE }),
        careService.workspaceDownloads.list({ workspaceId, documentScope: INTERNAL_SCOPE }),
      ]);
      setPublicDownloads(Array.isArray(publicData) ? publicData : []);
      setInternalDownloads(Array.isArray(internalData) ? internalData : []);
    } catch (error) {
      console.error("Failed to load workspace downloads", error);
      message.error(error?.message || "Failed to load downloads.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadDownloads();
  }, [loadDownloads]);

  useEffect(() => {
    const scopeParam = String(searchParams.get("scope") || INTERNAL_SCOPE).toUpperCase();
    const nextScope = scopeParam === PUBLIC_SCOPE ? PUBLIC_SCOPE : INTERNAL_SCOPE;
    const folderParam = searchParams.get("folder");
    const fileParam = searchParams.get("file");
    const targetKey = `${nextScope}:${folderParam || ""}:${fileParam || ""}:${publicDownloads.length}:${internalDownloads.length}`;
    if (appliedShareTargetRef.current === targetKey) return;

    const targetDownloads = nextScope === PUBLIC_SCOPE ? publicDownloads : internalDownloads;
    if ((folderParam || fileParam) && !targetDownloads.length) return;

    appliedShareTargetRef.current = targetKey;
    if (activeScope !== nextScope) setActiveScope(nextScope);

    if (fileParam) {
      const file = targetDownloads.find((item) => item.id === fileParam && isFile(item));
      if (!file) return;
      const parentId = file.parentId || null;
      setCurrentFolderIds((current) => ({ ...current, [nextScope]: parentId }));
      const siblingFiles = targetDownloads.filter((item) => isFile(item) && (item.parentId || null) === parentId);
      const fileIndex = siblingFiles.findIndex((item) => item.id === file.id);
      if (fileIndex >= 0) {
        setPreviewGallerySource("saved");
        setPreviewFileIndex(fileIndex);
        setPreviewRotation(0);
      }
      return;
    }

    if (folderParam) {
      const folder = targetDownloads.find((item) => item.id === folderParam && isFolder(item));
      if (!folder) return;
      setCurrentFolderIds((current) => ({ ...current, [nextScope]: folder.id }));
      return;
    }

    setCurrentFolderIds((current) => ({ ...current, [nextScope]: null }));
  }, [activeScope, internalDownloads, publicDownloads, searchParams]);

  useEffect(() => () => {
    selectedPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    remoteBlobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    selectedPreviewUrlsRef.current = [];
    remoteBlobUrlsRef.current = [];
    remotePreviewBlobUrlRef.current = "";
  }, []);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const closeMenu = () => setContextMenu(null);
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (remotePreviewBlobUrlRef.current) {
      URL.revokeObjectURL(remotePreviewBlobUrlRef.current);
      remoteBlobUrlsRef.current = remoteBlobUrlsRef.current.filter((url) => url !== remotePreviewBlobUrlRef.current);
      remotePreviewBlobUrlRef.current = "";
    }
    setRemotePreviewBlobUrl("");
    setRemotePreviewLoading(false);

    if (!previewFileSourceUrl || previewFileSourceUrl.startsWith("blob:")) return undefined;

    const controller = new AbortController();
    setRemotePreviewLoading(true);
    fetch(previewFileSourceUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load file preview.");
        return response.blob();
      })
      .then((blob) => {
        if (controller.signal.aborted) return;
        const blobUrl = URL.createObjectURL(blob);
        remotePreviewBlobUrlRef.current = blobUrl;
        remoteBlobUrlsRef.current.push(blobUrl);
        setRemotePreviewBlobUrl(blobUrl);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error("Failed to load workspace download preview", error);
        message.error(error?.message || "Unable to load file preview.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setRemotePreviewLoading(false);
      });

    return () => controller.abort();
  }, [previewFileSourceUrl]);

  const addDownloadFiles = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;
    if (!workspaceId) {
      message.error("Select a workspace before uploading downloads.");
      return;
    }
    if (!canUpload) {
      message.error("This workspace already has the maximum of 50 downloadable files.");
      return;
    }
    if (selectedFiles.length > slotsRemaining) {
      message.error(`Only ${slotsRemaining} upload slot${slotsRemaining === 1 ? "" : "s"} remaining for this workspace.`);
      return;
    }
    setProcessingFiles(true);
    try {
      const nextFiles = [];
      for (const [index, selectedFile] of selectedFiles.entries()) {
        const conversionResult = await convertHeicToJpegResult(selectedFile);
        const file = conversionResult.file;
        if (file.size > MAX_FILE_BYTES) {
          throw new Error(`${selectedFile.name} is larger than 5 MB after preprocessing.`);
        }
        const previewUrl = URL.createObjectURL(file);
        selectedPreviewUrlsRef.current.push(previewUrl);
        nextFiles.push({
          id: `${file.name}-${file.lastModified}-${file.size}-${Date.now()}-${index}`,
          file,
          fileName: file.name || selectedFile.name,
          description: "",
          originalFileName: selectedFile.name,
          convertedFromHeic: conversionResult.converted,
          conversionWarning: conversionWarningFor(conversionResult),
          previewUrl,
          type: file.type,
          contentType: file.type,
          size: file.size,
        });
      }
      const conversionWarning = nextFiles.map((file) => file.conversionWarning).find(Boolean);
      if (conversionWarning) message.warning(conversionWarning);
      setSelectedDownloadFiles((current) => [...current, ...nextFiles]);
    } catch (error) {
      message.error(error?.message || "Unable to preprocess selected documents.");
    } finally {
      setProcessingFiles(false);
    }
  };

  const updateSelectedDownloadFile = (fileId, field, value) => {
    setSelectedDownloadFiles((items) => items.map((item) => item.id === fileId ? { ...item, [field]: value } : item));
  };

  const removeSelectedDownloadFile = (fileId) => {
    setSelectedDownloadFiles((items) => {
      const removed = items.find((item) => item.id === fileId);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
        selectedPreviewUrlsRef.current = selectedPreviewUrlsRef.current.filter((url) => url !== removed.previewUrl);
      }
      if (previewGallerySource === "staged") {
        setPreviewFileIndex(-1);
        setPreviewGallerySource("");
      }
      return items.filter((item) => item.id !== fileId);
    });
  };

  const clearSelectedDownloadFiles = () => {
    selectedDownloadFiles.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    selectedPreviewUrlsRef.current = [];
    setSelectedDownloadFiles([]);
    if (previewGallerySource === "staged") {
      setPreviewFileIndex(-1);
      setPreviewGallerySource("");
    }
  };

  const uploadSelectedDownloadFiles = async () => {
    if (!selectedDownloadFiles.length) {
      message.error("Add at least one document before uploading.");
      return;
    }
    const missingName = selectedDownloadFiles.find((item) => !String(item.fileName || "").trim());
    if (missingName) {
      message.error("Document name is required for every file.");
      return;
    }

    setSaving(true);
    try {
      for (const selectedFile of selectedDownloadFiles) {
        const preparedFile = selectedFile.file;
        const uploadName = String(selectedFile.fileName || preparedFile.name).trim();
        const fileForUpload = preparedFile.name === uploadName
          ? preparedFile
          : new File([preparedFile], uploadName, {
            type: preparedFile.type,
            lastModified: preparedFile.lastModified || Date.now(),
          });
        const result = await uploadFile(null, fileForUpload, {
          confirmUpload: true,
          includeUserId: false,
          purpose: "OTHER",
          skipUserValidation: true,
          fileType: isPhoto(fileForUpload) ? "PHOTO" : fileForUpload.type || "DOCUMENT",
          preserveOriginal: true,
        });
        await careService.workspaceDownloads.create({
          workspaceId,
          parentId: currentFolderId,
          fileId: result.fileId,
          fileName: uploadName,
          description: String(selectedFile.description || "").trim(),
          mimeType: fileForUpload.type || selectedFile.type || "application/octet-stream",
          fileSizeBytes: fileForUpload.size,
          storageFileType: isPhoto(fileForUpload) ? "PHOTO" : "DOCUMENT",
          documentScope: INTERNAL_SCOPE,
        });
      }
      message.success(`${selectedDownloadFiles.length} file${selectedDownloadFiles.length === 1 ? "" : "s"} uploaded.`);
      clearSelectedDownloadFiles();
      await loadDownloads();
    } catch (error) {
      console.error("Failed to upload workspace download", error);
      message.error(error?.message || "Failed to upload file.");
    } finally {
      setSaving(false);
    }
  };

  const openFolderDialog = (parentId = currentFolderId) => {
    setFolderDraft({ fileName: "", description: "", parentId: parentId || null });
    setFolderError("");
    setShowFolderDialog(true);
  };

  const closeFolderDialog = () => {
    if (saving) return;
    setShowFolderDialog(false);
    setFolderError("");
  };

  const createDirectory = async (event) => {
    event?.preventDefault();
    const name = String(folderDraft.fileName || "").trim();
    if (!name) {
      setFolderError("Folder name is required.");
      return;
    }
    if (activeScope === INTERNAL_SCOPE && !workspaceId) {
      message.error("Select a workspace before creating a directory.");
      return;
    }
    setSaving(true);
    setFolderError("");
    try {
      const created = await careService.workspaceDownloads.createDirectory({
        workspaceId: activeScope === INTERNAL_SCOPE ? workspaceId : null,
        parentId: Object.prototype.hasOwnProperty.call(folderDraft, "parentId") ? folderDraft.parentId : currentFolderId,
        fileName: name,
        description: String(folderDraft.description || "").trim(),
        documentScope: activeScope,
        itemType: "FOLDER",
      });
      if (activeScope === PUBLIC_SCOPE) {
        setPublicDownloads((items) => [created, ...items]);
      } else {
        setInternalDownloads((items) => [created, ...items]);
      }
      setShowFolderDialog(false);
      setFolderDraft({ fileName: "", description: "", parentId: null });
      message.success("Directory created.");
    } catch (error) {
      console.error("Failed to create downloads directory", error);
      setFolderError(error?.message || "Failed to create directory.");
    } finally {
      setSaving(false);
    }
  };

  const updateDownloadField = (downloadId, field, value) => {
    setInternalDownloads((items) => items.map((item) => item.id === downloadId ? { ...item, [field]: value } : item));
  };

  const saveDownloadDetails = async (download) => {
    if (!download?.id) return;
    if (download.documentScope === PUBLIC_SCOPE) return;
    const fileNameValue = String(download.fileName || "").trim();
    if (!fileNameValue) {
      message.error("File name is required.");
      await loadDownloads();
      return;
    }
    try {
      await careService.workspaceDownloads.update(download.id, {
        fileName: fileNameValue,
        description: String(download.description || "").trim(),
        documentScope: INTERNAL_SCOPE,
        parentId: download.parentId || null,
        itemType: download.itemType || "FILE",
      });
    } catch (error) {
      console.error("Failed to update workspace download", error);
      message.error(error?.message || "Failed to update download details.");
      await loadDownloads();
    }
  };

  const isDescendantFolder = (folderId, possibleAncestorId) => {
    const seen = new Set();
    let cursor = folderId;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      if (cursor === possibleAncestorId) return true;
      const lookupId = cursor;
      const folder = downloads.find((item) => item.id === lookupId && isFolder(item));
      cursor = folder?.parentId || null;
    }
    return false;
  };

  const moveDownloadToFolder = async (download, targetParentId) => {
    if (!download?.id || download.documentScope === PUBLIC_SCOPE) return;
    const nextParentId = targetParentId || null;
    if ((download.parentId || null) === nextParentId) return;
    if (isFolder(download) && (download.id === nextParentId || isDescendantFolder(nextParentId, download.id))) {
      message.error("A folder cannot be moved into itself or one of its subfolders.");
      return;
    }

    const previousDownloads = internalDownloads;
    setInternalDownloads((items) => items.map((item) => item.id === download.id ? { ...item, parentId: nextParentId } : item));
    try {
      await careService.workspaceDownloads.update(download.id, {
        fileName: String(download.fileName || "").trim() || (isFolder(download) ? "Untitled folder" : "Untitled document"),
        description: String(download.description || "").trim(),
        documentScope: INTERNAL_SCOPE,
        parentId: nextParentId,
        itemType: download.itemType || (isFolder(download) ? "FOLDER" : "FILE"),
      });
      message.success(`${isFolder(download) ? "Folder" : "Document"} moved.`);
    } catch (error) {
      console.error("Failed to move workspace download", error);
      setInternalDownloads(previousDownloads);
      message.error(error?.message || "Failed to move document.");
    }
  };

  const dragDownloadFromEvent = (event) => {
    const id = event.dataTransfer.getData("application/x-workspace-download-id") || draggingDownloadId;
    return downloads.find((item) => item.id === id);
  };

  const handleDownloadDragStart = (event, download) => {
    if (activeScope !== INTERNAL_SCOPE || download.documentScope === PUBLIC_SCOPE) return;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-workspace-download-id", download.id);
    setDraggingDownloadId(download.id);
  };

  const handleDownloadDragEnd = () => {
    setDraggingDownloadId("");
    setDragOverFolderId("");
  };

  const handleFolderDragOver = (event, folder) => {
    if (activeScope !== INTERNAL_SCOPE || folder.documentScope === PUBLIC_SCOPE) return;
    const dragged = dragDownloadFromEvent(event);
    if (!dragged || dragged.id === folder.id || (isFolder(dragged) && isDescendantFolder(folder.id, dragged.id))) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverFolderId(folder.id);
  };

  const handleFolderDrop = async (event, folder) => {
    event.preventDefault();
    event.stopPropagation();
    const dragged = dragDownloadFromEvent(event);
    setDragOverFolderId("");
    if (!dragged || !folder?.id) return;
    await moveDownloadToFolder(dragged, folder.id);
    setDraggingDownloadId("");
  };

  const handleRootDragOver = (event) => {
    if (activeScope !== INTERNAL_SCOPE) return;
    const dragged = dragDownloadFromEvent(event);
    if (!dragged || !(dragged.parentId || null)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverFolderId("root");
  };

  const handleRootDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const dragged = dragDownloadFromEvent(event);
    setDragOverFolderId("");
    if (!dragged) return;
    await moveDownloadToFolder(dragged, null);
    setDraggingDownloadId("");
  };

  const openDeleteDialog = (download) => {
    if (!download?.id) return;
    setDeleteCandidate(download);
    setDeleteError("");
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteCandidate(null);
    setDeleteError("");
  };

  const deleteDownload = async () => {
    if (!deleteCandidate?.id) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await careService.workspaceDownloads.delete(deleteCandidate.id);
      setInternalDownloads((items) => items.filter((item) => item.id !== deleteCandidate.id));
      message.success(`${isFolder(deleteCandidate) ? "Folder" : "Document"} removed.`);
      setDeleteCandidate(null);
    } catch (error) {
      console.error("Failed to delete workspace download", error);
      setDeleteError(error?.message || "Failed to delete document.");
    } finally {
      setDeleting(false);
    }
  };

  const blobUrlForDownload = async (download) => {
    const url = documentUrlFor(download);
    if (!url) {
      throw new Error("Download URL is not available yet. Refresh and try again.");
    }
    if (url.startsWith("blob:")) return url;
    const response = await fetch(url);
    if (!response.ok) throw new Error("Unable to load file.");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    remoteBlobUrlsRef.current.push(blobUrl);
    return blobUrl;
  };

  const openDownload = async (download) => {
    try {
      const blobUrl = await blobUrlForDownload(download);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open workspace download as blob", error);
      message.error(error?.message || "Failed to open file.");
    }
  };

  const openPreviewGallery = (source, index) => {
    setPreviewGallerySource(source);
    setPreviewFileIndex(index);
    setPreviewRotation(0);
  };

  const closePreviewGallery = () => {
    setPreviewGallerySource("");
    setPreviewFileIndex(-1);
    setPreviewRotation(0);
  };

  const showPreviewAt = (index) => {
    if (!previewGalleryItems.length) return;
    setPreviewFileIndex((index + previewGalleryItems.length) % previewGalleryItems.length);
    setPreviewRotation(0);
  };

  const rotatePreview = (degrees) => {
    setPreviewRotation((current) => (current + degrees + 360) % 360);
  };

  const openDocumentContextMenu = (event, target = {}) => {
    event.preventDefault();
    event.stopPropagation();
    const padding = 12;
    const menuWidth = 238;
    const menuHeight = 220;
    setContextMenu({
      x: Math.min(event.clientX, window.innerWidth - menuWidth - padding),
      y: Math.min(event.clientY, window.innerHeight - menuHeight - padding),
      target,
    });
  };

  const contextMenuTargetItem = contextMenu?.target?.item || null;
  const contextMenuIsFile = contextMenuTargetItem && isFile(contextMenuTargetItem);
  const contextMenuIsFolder = contextMenuTargetItem && isFolder(contextMenuTargetItem);

  const runContextMenuAction = async (action) => {
    const target = contextMenu?.target || {};
    const item = target.item;
    const targetFolderId = Object.prototype.hasOwnProperty.call(target, "folderId") ? target.folderId : currentFolderId;
    setContextMenu(null);
    if (action === "new-folder") {
      if (activeScope !== INTERNAL_SCOPE) return;
      openFolderDialog(targetFolderId);
      return;
    }
    if (action === "upload") {
      if (activeScope !== INTERNAL_SCOPE) return;
      setCurrentFolderIds((current) => ({ ...current, [activeScope]: targetFolderId || null }));
      setShowUploader(true);
      return;
    }
    if (action === "share") {
      await copyDocumentLink(target);
      return;
    }
    if (action === "open-folder" && item && isFolder(item)) {
      setCurrentFolder(item.id);
      return;
    }
    if (action === "download" && item && isFile(item)) {
      await openDownload(item);
      return;
    }
    if (action === "delete" && item) {
      openDeleteDialog(item);
    }
  };

  return (
    <div className="workspace-downloads-page">
      <div className="page-heading-row">
        <div>
          <div className="crumbs">Workspace <i className="ti ti-chevron-right" /> Documents</div>
          <h1>Documents</h1>
          <p>Public Oliveeb documents are shared across workspaces. Internal documents belong only to {selectedWorkspace?.name || "this workspace"}.</p>
        </div>
        <div className="page-heading-actions">
          <button className="btn secondary" type="button" onClick={loadDownloads} disabled={loading}>
            <i className={`ti ti-refresh${loading ? " spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <section className="workspace-downloads-list">
        <div className="workspace-download-scope-tabs" role="tablist" aria-label="Download document scope">
          <button
            className={activeScope === PUBLIC_SCOPE ? "active" : ""}
            type="button"
            onClick={() => {
              setActiveScope(PUBLIC_SCOPE);
              setShowUploader(false);
            }}
            role="tab"
            id="downloads-tab-public"
            aria-controls="downloads-panel-public"
            aria-selected={activeScope === PUBLIC_SCOPE}
            tabIndex={activeScope === PUBLIC_SCOPE ? 0 : -1}
          >
            Public Documents <span>{publicDownloads.length}</span>
          </button>
          <button
            className={activeScope === INTERNAL_SCOPE ? "active" : ""}
            type="button"
            onClick={() => setActiveScope(INTERNAL_SCOPE)}
            role="tab"
            id="downloads-tab-internal"
            aria-controls="downloads-panel-internal"
            aria-selected={activeScope === INTERNAL_SCOPE}
            tabIndex={activeScope === INTERNAL_SCOPE ? 0 : -1}
          >
            Internal Documents <span>{internalDownloads.length}</span>
          </button>
        </div>
        <div
          className="workspace-download-tab-panel"
          id={activeScope === PUBLIC_SCOPE ? "downloads-panel-public" : "downloads-panel-internal"}
          role="tabpanel"
          aria-labelledby={activeScope === PUBLIC_SCOPE ? "downloads-tab-public" : "downloads-tab-internal"}
        >
          <div className="workspace-download-panel-toolbar">
            <div>
              <h2>{activeScope === PUBLIC_SCOPE ? "Public Documents" : "Internal Documents"}</h2>
              <p>{activeScope === PUBLIC_SCOPE ? "Shared Oliveeb documents available across workspaces." : "Workspace files available only to this care workspace."}</p>
            </div>
            <div className="workspace-download-panel-actions">
              <div className="workspace-download-view-toggle" aria-label="Document view layout">
                {DOCUMENT_VIEW_MODES.map((mode) => (
                  <button
                    className={viewMode === mode.id ? "active" : ""}
                    key={mode.id}
                    type="button"
                    onClick={() => setViewMode(mode.id)}
                    aria-pressed={viewMode === mode.id}
                  >
                    <i className={`ti ${mode.icon}`} /> {mode.label}
                  </button>
                ))}
              </div>
              {activeScope === INTERNAL_SCOPE ? (
                <>
                  <button className="btn workspace-folder-button" type="button" onClick={openFolderDialog} disabled={saving || uploading || processingFiles}>
                    <i className="ti ti-folder-plus" /> New Folder
                  </button>
                  <button className="btn primary" type="button" onClick={() => setShowUploader(true)} disabled={!canUpload || saving || uploading || processingFiles}>
                    <i className="ti ti-plus" /> Upload New Document
                  </button>
                </>
              ) : null}
              <span>{loading ? "Loading..." : `${downloads.filter(isFile).length} files · ${downloads.filter(isFolder).length} folders`}</span>
            </div>
          </div>
          <div className="workspace-download-breadcrumbs" aria-label="Download directory path">
            <button
              className={dragOverFolderId === "root" ? "is-drop-target" : ""}
              type="button"
              onClick={() => setCurrentFolder(null)}
              onContextMenu={(event) => openDocumentContextMenu(event, { scope: activeScope, folderId: null })}
              onDragOver={handleRootDragOver}
              onDragLeave={() => setDragOverFolderId("")}
              onDrop={handleRootDrop}
            >
              <i className="ti ti-home" /> Root
            </button>
            {folderPath.map((folder) => (
              <button
                type="button"
                key={folder.id}
                onClick={() => setCurrentFolder(folder.id)}
                onContextMenu={(event) => openDocumentContextMenu(event, { scope: activeScope, item: folder })}
              >
                <i className="ti ti-chevron-right" /> {folder.fileName}
              </button>
            ))}
          </div>
        {activeScope === INTERNAL_SCOPE && showUploader ? (
          <section className="workspace-downloads-upload workspace-downloads-upload--assessment">
            <div className="workspace-download-upload-head">
              <div>
                <h2>Upload Documents</h2>
                <p>Internal workspace documents only. Maximum 5 MB per file.</p>
              </div>
              <div className="workspace-download-upload-actions">
                <button className="btn primary state-assessment-upload-button" type="button" onClick={uploadSelectedDownloadFiles} disabled={saving || uploading || processingFiles || !selectedDownloadFiles.length}>
                  <i className={saving || uploading ? "ti ti-loader-2 spin" : "ti ti-cloud-upload"} /> {saving || uploading ? "Uploading..." : "Upload"}
                </button>
                <button className="btn secondary state-assessment-upload-button" type="button" onClick={() => setShowUploader(false)} disabled={saving || uploading || processingFiles}>
                  <i className="ti ti-check" /> Done
                </button>
              </div>
            </div>
            <label className="assessment-upload assessment-upload-compact workspace-downloads-dropzone">
              <input
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*"
                className="assessment-file-input"
                multiple
                type="file"
                onChange={addDownloadFiles}
                disabled={!canUpload || saving || uploading || processingFiles}
              />
              <i className={saving || uploading || processingFiles ? "ti ti-loader-2 spin" : "ti ti-file-upload"} />
              <strong>{processingFiles ? "Preparing files..." : saving || uploading ? "Uploading files..." : "Add downloadable files"}</strong>
              <small>PDF, Office, text, or image files. Add files here, then enter document names and descriptions below.</small>
            </label>
            <div className="workspace-downloads-limit">
              <strong>{internalFileCount + selectedDownloadFiles.length} of {MAX_WORKSPACE_FILES}</strong>
              <span>{slotsRemaining} upload slot{slotsRemaining === 1 ? "" : "s"} remaining</span>
            </div>
            <section className="workspace-download-staged-card">
              <h3>Documents</h3>
              <p>Files upload after you click Upload. Add names and descriptions before saving them as internal workspace downloads.</p>
              <div className="workspace-download-staged-list assessment-uploaded-doc-list state-assessment-page-list">
                {selectedDownloadFiles.map((item, index) => (
                  <div className="workspace-download-staged-row assessment-uploaded-doc-row state-assessment-page-row" key={item.id}>
                    <button className={`workspace-download-thumb ${isPhoto(item) ? "photo" : "document"}`} type="button" onClick={() => openPreviewGallery("staged", selectedDownloadFiles.findIndex((file) => file.id === item.id))}>
                      {isPhoto(item) && item.previewUrl ? (
                        <img src={item.previewUrl} alt="" />
                      ) : (
                        <i className={`ti ${isPhoto(item) ? "ti-photo" : "ti-file-description"}`} />
                      )}
                    </button>
                    <strong>
                      Document {index + 1}
                      <small>{downloadFileTypeLabel(item)} - {bytesLabel(item.size)} - source: {item.originalFileName}</small>
                    </strong>
                    <div className="workspace-download-staged-fields">
                      <input
                        value={item.fileName || ""}
                        onChange={(event) => updateSelectedDownloadFile(item.id, "fileName", event.target.value)}
                        placeholder="Document name"
                        aria-label="Document name"
                      />
                      <textarea
                        value={item.description || ""}
                        onChange={(event) => updateSelectedDownloadFile(item.id, "description", event.target.value)}
                        placeholder="Description"
                        rows={2}
                        aria-label="Document description"
                      />
                    </div>
                    <span className="assessment-document-actions">
                      <button type="button" onClick={() => openPreviewGallery("staged", index)}><i className="ti ti-eye" /></button>
                      <button type="button" onClick={() => removeSelectedDownloadFile(item.id)} disabled={saving || uploading || processingFiles}><i className="ti ti-trash" /></button>
                    </span>
                  </div>
                ))}
                {!selectedDownloadFiles.length ? (
                  <div className="assessment-empty-state">No documents added yet.</div>
                ) : null}
              </div>
            </section>
          </section>
        ) : null}
        {currentItems.length ? (
          <div
            className={`workspace-downloads-table workspace-downloads-table--${viewMode}`}
            onContextMenu={(event) => openDocumentContextMenu(event, { scope: activeScope, folderId: currentFolderId })}
          >
            {currentFolders.map((download) => {
              const childCount = downloads.filter((item) => item.parentId === download.id).length;
              return (
                <div
                  className={`workspace-download-row workspace-download-row--folder${draggingDownloadId === download.id ? " is-dragging" : ""}${dragOverFolderId === download.id ? " is-drop-target" : ""}`}
                  key={download.id}
                  draggable={activeScope === INTERNAL_SCOPE && download.documentScope !== PUBLIC_SCOPE}
                  onDragStart={(event) => handleDownloadDragStart(event, download)}
                  onDragEnd={handleDownloadDragEnd}
                  onDragOver={(event) => handleFolderDragOver(event, download)}
                  onDragEnter={(event) => handleFolderDragOver(event, download)}
                  onDragLeave={() => setDragOverFolderId("")}
                  onDrop={(event) => handleFolderDrop(event, download)}
                  onContextMenu={(event) => openDocumentContextMenu(event, { scope: activeScope, item: download })}
                >
                  <button
                    className="workspace-download-thumb folder"
                    type="button"
                    onClick={() => setCurrentFolder(download.id)}
                  >
                    <i className="ti ti-folder" />
                  </button>
                  <div>
                    <strong>{download.fileName || "Directory"}</strong>
                    {!isCompactView ? (
                      <>
                        <p>{download.description || `${childCount} item${childCount === 1 ? "" : "s"}`}</p>
                        <small><i className="ti ti-grip-vertical" /> Folder · Drag items here · Added {formatDateTime(download.createdAt)}</small>
                      </>
                    ) : null}
                  </div>
                  <div className="workspace-download-actions">
                    <button className={`mini-btn${isCompactView ? " icon-only" : ""}`} type="button" onClick={() => setCurrentFolder(download.id)} aria-label={`Open ${download.fileName || "folder"}`}>
                      <i className="ti ti-folder-open" /> {!isCompactView ? "Open" : null}
                    </button>
                    {download.documentScope !== PUBLIC_SCOPE ? (
                      <button className={`mini-btn danger${isCompactView ? " icon-only" : ""}`} type="button" onClick={() => openDeleteDialog(download)} aria-label={`Delete ${download.fileName || "folder"}`}>
                        <i className="ti ti-trash" /> {!isCompactView ? "Delete" : null}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {currentFiles.map((download, index) => {
              const photo = isPhoto(download);
              return (
                <div
                  className={`workspace-download-row${draggingDownloadId === download.id ? " is-dragging" : ""}`}
                  key={download.id}
                  draggable={activeScope === INTERNAL_SCOPE && download.documentScope !== PUBLIC_SCOPE}
                  onDragStart={(event) => handleDownloadDragStart(event, download)}
                  onDragEnd={handleDownloadDragEnd}
                  onContextMenu={(event) => openDocumentContextMenu(event, { scope: activeScope, item: download })}
                >
                  <button className="workspace-download-thumb" type="button" onClick={() => openPreviewGallery("saved", index)}>
                    {photo && (download.previewUrl || download.signedUrl) ? (
                      <img src={download.previewUrl || download.signedUrl} alt="" />
                    ) : (
                      <i className={`ti ${photo ? "ti-photo" : "ti-file-description"}`} />
                    )}
                  </button>
                  <div>
                    {isCompactView ? (
                      <strong>{download.fileName || "Document"}</strong>
                    ) : download.documentScope === PUBLIC_SCOPE ? (
                      <>
                        <strong>{download.fileName || "Public document"}</strong>
                        <p>{download.description || "Oliveeb public document"}</p>
                      </>
                    ) : (
                      <>
                        <input
                          className="workspace-download-inline-input"
                          value={download.fileName || ""}
                          onBlur={() => saveDownloadDetails(download)}
                          onChange={(event) => updateDownloadField(download.id, "fileName", event.target.value)}
                          aria-label="Download file name"
                        />
                        <textarea
                          className="workspace-download-inline-input workspace-download-inline-textarea"
                          value={download.description || ""}
                          onBlur={() => saveDownloadDetails(download)}
                          onChange={(event) => updateDownloadField(download.id, "description", event.target.value)}
                          placeholder="Description"
                          rows={1}
                          aria-label="Download description"
                        />
                      </>
                    )}
                    {!isCompactView ? <small>{bytesLabel(download.fileSizeBytes)} · Added {formatDateTime(download.createdAt)}</small> : null}
                  </div>
                  <div className="workspace-download-actions">
                    {photo && !isCompactView ? (
                      <button className="mini-btn" type="button" onClick={() => openPreviewGallery("saved", index)}>
                        <i className="ti ti-eye" /> Preview
                      </button>
                    ) : null}
                    <button className={`mini-btn${isCompactView ? " icon-only" : ""}`} type="button" onClick={() => openDownload(download)} aria-label={`Download ${download.fileName || "document"}`}>
                      <i className="ti ti-download" /> {!isCompactView ? "Download" : null}
                    </button>
                    {download.documentScope !== PUBLIC_SCOPE ? (
                      <button className={`mini-btn danger${isCompactView ? " icon-only" : ""}`} type="button" onClick={() => openDeleteDialog(download)} aria-label={`Delete ${download.fileName || "document"}`}>
                        <i className="ti ti-trash" /> {!isCompactView ? "Delete" : null}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state compact" onContextMenu={(event) => openDocumentContextMenu(event, { scope: activeScope, folderId: currentFolderId })}>
            <i className="ti ti-download" />
            <strong>No items in this directory</strong>
            <span>{activeScope === PUBLIC_SCOPE ? "Public Oliveeb documents will appear here when uploaded from the portal." : "Create a folder or upload the first workspace file here."}</span>
          </div>
        )}
        </div>
      </section>

      {contextMenu ? (
        <div
          className="workspace-document-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          role="menu"
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
        >
          <div className="workspace-document-context-menu__title">
            <i className={`ti ${contextMenuIsFolder ? "ti-folder" : contextMenuIsFile ? "ti-file-description" : "ti-folder-open"}`} />
            <span>{contextMenuTargetItem?.fileName || (currentFolderId ? "Current folder" : "Root")}</span>
          </div>
          {contextMenuIsFolder ? (
            <button type="button" role="menuitem" onClick={() => runContextMenuAction("open-folder")}>
              <i className="ti ti-folder-open" /> Open folder
            </button>
          ) : null}
          {activeScope === INTERNAL_SCOPE && !contextMenuTargetItem ? (
            <>
              <button type="button" role="menuitem" onClick={() => runContextMenuAction("new-folder")}>
                <i className="ti ti-folder-plus" /> New folder
              </button>
              <button type="button" role="menuitem" onClick={() => runContextMenuAction("upload")}>
                <i className="ti ti-file-upload" /> Upload file
              </button>
            </>
          ) : null}
          <button type="button" role="menuitem" onClick={() => runContextMenuAction("share")}>
            <i className="ti ti-link" /> Copy share link
          </button>
          {contextMenuIsFile ? (
            <button type="button" role="menuitem" onClick={() => runContextMenuAction("download")}>
              <i className="ti ti-download" /> Download
            </button>
          ) : null}
          {activeScope === INTERNAL_SCOPE && contextMenuTargetItem ? (
            <button className="danger" type="button" role="menuitem" onClick={() => runContextMenuAction("delete")}>
              <i className="ti ti-trash" /> Delete
            </button>
          ) : null}
        </div>
      ) : null}

      {deleteCandidate ? (
        <div className="download-preview-backdrop workspace-delete-backdrop" role="presentation" onClick={closeDeleteDialog}>
          <div className="workspace-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-document-title" onClick={(event) => event.stopPropagation()}>
            <div className="workspace-delete-dialog__header">
              <span className="workspace-delete-dialog__icon"><i className="ti ti-trash" /></span>
              <div>
                <strong id="delete-document-title">Delete {isFolder(deleteCandidate) ? "folder" : "document"}?</strong>
                <small>{deleteCandidate.fileName || (isFolder(deleteCandidate) ? "Untitled folder" : "Untitled document")}</small>
              </div>
              <button className="icon-btn" type="button" onClick={closeDeleteDialog} aria-label="Close delete confirmation" disabled={deleting}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="workspace-delete-dialog__body">
              <p>This will remove it from internal workspace documents.</p>
              {isFolder(deleteCandidate) ? (
                <p className="workspace-delete-dialog__note">Folders must be empty before they can be deleted.</p>
              ) : null}
              {deleteError ? <p className="form-error">{deleteError}</p> : null}
            </div>
            <div className="workspace-delete-dialog__actions">
              <button className="btn secondary" type="button" onClick={closeDeleteDialog} disabled={deleting}>
                Keep item
              </button>
              <button className="btn workspace-delete-button" type="button" onClick={deleteDownload} disabled={deleting}>
                <i className={`ti ${deleting ? "ti-loader-2 spin" : "ti-trash"}`} /> {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showFolderDialog ? (
        <div className="download-preview-backdrop workspace-folder-backdrop" role="presentation" onClick={closeFolderDialog}>
          <form className="workspace-folder-dialog" role="dialog" aria-modal="true" aria-labelledby="new-folder-title" onSubmit={createDirectory} onClick={(event) => event.stopPropagation()}>
            <div className="workspace-folder-dialog__header">
              <span className="workspace-folder-dialog__icon"><i className="ti ti-folder-plus" /></span>
              <div>
                <strong id="new-folder-title">New folder</strong>
                <small>{folderDialogPath.length ? folderDialogPath.map((folder) => folder.fileName).join(" / ") : "Root"} directory</small>
              </div>
              <button className="icon-btn" type="button" onClick={closeFolderDialog} aria-label="Close new folder dialog" disabled={saving}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="workspace-folder-dialog__body">
              <label>
                <span>Folder name</span>
                <input
                  autoFocus
                  type="text"
                  value={folderDraft.fileName}
                  maxLength={120}
                  placeholder="Compliance documents"
                  onChange={(event) => {
                    setFolderDraft((current) => ({ ...current, fileName: event.target.value }));
                    if (folderError) setFolderError("");
                  }}
                />
              </label>
              <label>
                <span>Description</span>
                <textarea
                  value={folderDraft.description}
                  maxLength={240}
                  placeholder="Optional note for this folder"
                  onChange={(event) => setFolderDraft((current) => ({ ...current, description: event.target.value }))}
                />
              </label>
              {folderError ? <p className="form-error">{folderError}</p> : null}
            </div>
            <div className="workspace-folder-dialog__actions">
              <button className="btn secondary" type="button" onClick={closeFolderDialog} disabled={saving}>
                Done
              </button>
              <button className="btn workspace-folder-button" type="submit" disabled={saving || !String(folderDraft.fileName || "").trim()}>
                <i className="ti ti-folder-plus" /> {saving ? "Creating..." : "Create Folder"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {previewFile ? (
        <div className="download-preview-backdrop" role="presentation" onClick={closePreviewGallery}>
          <div className="download-preview-dialog" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="download-preview-header">
              <div>
                <strong>{previewFile.fileName || previewFile.name || "Document"}</strong>
                <small>{previewFile.description || "Workspace document"} · {previewFileIndex + 1} of {previewGalleryItems.length}</small>
              </div>
              <div>
                <button className="mini-btn icon-only" type="button" onClick={() => showPreviewAt(previewFileIndex - 1)} disabled={!canNavigatePreview} aria-label="Previous document">
                  <i className="ti ti-chevron-left" />
                </button>
                <button className="mini-btn icon-only" type="button" onClick={() => showPreviewAt(previewFileIndex + 1)} disabled={!canNavigatePreview} aria-label="Next document">
                  <i className="ti ti-chevron-right" />
                </button>
                <button className="mini-btn icon-only" type="button" onClick={() => rotatePreview(-90)} disabled={!isPhoto(previewFile)} aria-label="Rotate left">
                  <i className="ti ti-rotate-2" />
                </button>
                <button className="mini-btn icon-only" type="button" onClick={() => rotatePreview(90)} disabled={!isPhoto(previewFile)} aria-label="Rotate right">
                  <i className="ti ti-rotate-clockwise-2" />
                </button>
                <button className="mini-btn" type="button" onClick={() => openDownload(previewFile)}>
                  <i className="ti ti-download" /> {previewGallerySource === "staged" ? "Open" : "Download"}
                </button>
                <button className="icon-btn" type="button" onClick={closePreviewGallery} aria-label="Close preview">
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            {remotePreviewLoading ? (
              <div className="empty-state compact">
                <i className="ti ti-loader-2 spin" />
                <strong>Preparing preview</strong>
                <span>Opening the file as a browser preview.</span>
              </div>
            ) : isPhoto(previewFile) && previewFileUrl ? (
              <img
                className="download-preview-image"
                src={previewFileUrl}
                style={{ transform: `rotate(${previewRotation}deg)` }}
                alt={previewFile.fileName || previewFile.name || "Workspace document"}
              />
            ) : previewFileUrl ? (
              <iframe
                className="download-preview-frame"
                src={previewFileUrl}
                title={previewFile.fileName || previewFile.name || "Workspace document"}
              />
            ) : (
              <div className="empty-state compact">
                <i className="ti ti-file-description" />
                <strong>{previewFile.fileName || previewFile.name || "Document"}</strong>
                <span>Preview is not available yet.</span>
              </div>
            )}
            {previewGalleryItems.length > 1 ? (
              <div className="download-preview-thumbnails">
                {previewGalleryItems.map((item, index) => (
                  <button
                    className={index === previewFileIndex ? "active" : ""}
                    key={item.id || `${item.fileName}-${index}`}
                    type="button"
                    onClick={() => showPreviewAt(index)}
                    aria-label={`Preview ${item.fileName || item.name || `document ${index + 1}`}`}
                  >
                    {isPhoto(item) && (item.previewUrl || item.signedUrl || item.downloadUrl) ? (
                      <img src={item.previewUrl || item.signedUrl || item.downloadUrl} alt="" />
                    ) : (
                      <i className="ti ti-file-description" />
                    )}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default WorkspaceDownloadsPage;
